import React, { useState, useMemo, useCallback } from 'react';
import {
  studyAreas,
  studyHotspots,
  defaultStudyId,
  PALMISTRY_HAND_SVG,
  PALMISTRY_HAND_SVG_FALLBACK,
  PALMISTRY_PREVIEW_STUDY_IDS
} from '../../data/palmistry101Content';

function groupOrder(group) {
  const order = { lines: 0, mounts: 1, structure: 2 };
  return order[group] ?? 9;
}

const GROUP_LABELS = {
  lines: 'Major lines',
  mounts: 'Mounts',
  structure: 'Structure'
};

export default function InteractiveHandStudy({ hasFullAccess = true, onRequestPurchase }) {
  const previewSet = useMemo(() => new Set(PALMISTRY_PREVIEW_STUDY_IDS), []);
  const initialId = hasFullAccess ? defaultStudyId : PALMISTRY_PREVIEW_STUDY_IDS[0] || defaultStudyId;

  const [selectedId, setSelectedId] = useState(initialId);
  const [imgSrc, setImgSrc] = useState(PALMISTRY_HAND_SVG);

  const selected = useMemo(
    () => studyAreas.find(a => a.id === selectedId) || studyAreas[0],
    [selectedId]
  );

  const showFullDetail = hasFullAccess || previewSet.has(selectedId);

  const hotspotsByGroup = useMemo(() => {
    const map = new Map();
    studyAreas.forEach(area => {
      const g = area.group || 'other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(area);
    });
    return map;
  }, []);

  const orderedGroups = useMemo(() => {
    return [...hotspotsByGroup.keys()].sort((a, b) => groupOrder(a) - groupOrder(b));
  }, [hotspotsByGroup]);

  const onPick = useCallback(
    id => {
      if (!hasFullAccess && !previewSet.has(id)) {
        setSelectedId(id);
        return;
      }
      setSelectedId(id);
    },
    [hasFullAccess, previewSet]
  );

  const onImgError = useCallback(() => {
    if (imgSrc !== PALMISTRY_HAND_SVG_FALLBACK) {
      setImgSrc(PALMISTRY_HAND_SVG_FALLBACK);
    }
  }, [imgSrc]);

  return (
    <div className="amm-palmistry101-hand-module">
      <div className="amm-palmistry101-hand-layout">
        <figure className="amm-palmistry101-hand-figure" aria-label="Interactive palm illustration">
          <div className="amm-palmistry101-hand-image-wrap">
            <img
              className="amm-palmistry101-hand-img"
              src={imgSrc}
              onError={onImgError}
              alt="Illustrated palm with palmistry lines, mounts, and astrological analogies"
              decoding="async"
            />
            <div className="amm-palmistry101-hand-hotspots" role="presentation">
              {studyAreas.map((area, idx) => {
                const box = studyHotspots[area.id];
                if (!box) return null;
                const isActive = area.id === selectedId;
                const isLocked = !hasFullAccess && !previewSet.has(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    className={`amm-palmistry101-hotspot${isActive ? ' is-active' : ''}${
                      isLocked ? ' is-locked' : ''
                    }`}
                    style={{
                      left: `${box.left}%`,
                      top: `${box.top}%`,
                      width: `${box.width}%`,
                      height: `${box.height}%`
                    }}
                    onClick={() => onPick(area.id)}
                    aria-pressed={isActive}
                    aria-label={isLocked ? `${area.label} (included in full course)` : area.label}
                  >
                    <span className="amm-palmistry101-hotspot-ring" aria-hidden />
                    <span className="amm-palmistry101-hotspot-num" aria-hidden>
                      {idx + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <figcaption className="amm-palmistry101-hand-caption">
            {hasFullAccess
              ? 'Tap a region or use the list beside the hand. This illustration is a teaching companion, not a medical diagram.'
              : 'Preview: explore two sample areas. The full course unlocks every line, mount, and structure note.'}
          </figcaption>
        </figure>

        <aside className="amm-palmistry101-hand-aside" aria-live="polite">
          <div className="amm-palmistry101-panel-nav">
            <p className="amm-palmistry101-panel-nav-title">Study areas</p>
            {!hasFullAccess && (
              <p className="amm-palmistry101-panel-preview-note">
                Sample areas are open; the rest unlock with the course.
              </p>
            )}
            <div className="amm-palmistry101-panel-nav-groups">
              {orderedGroups.map(group => (
                <div key={group} className="amm-palmistry101-nav-group">
                  <p className="amm-palmistry101-nav-group-label">{GROUP_LABELS[group] || group}</p>
                  <ul className="amm-palmistry101-nav-list">
                    {hotspotsByGroup.get(group).map(area => {
                      const idx = studyAreas.findIndex(a => a.id === area.id) + 1;
                      const isOn = area.id === selectedId;
                      const isLocked = !hasFullAccess && !previewSet.has(area.id);
                      return (
                        <li key={area.id}>
                          <button
                            type="button"
                            className={`amm-palmistry101-nav-btn${isOn ? ' is-active' : ''}${
                              isLocked ? ' is-locked' : ''
                            }`}
                            onClick={() => onPick(area.id)}
                          >
                            <span className="amm-palmistry101-nav-num">{idx}</span>
                            <span className="amm-palmistry101-nav-label">{area.label}</span>
                            {isLocked && <span className="amm-palmistry101-nav-lock" aria-hidden />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="amm-palmistry101-detail-panel">
            {showFullDetail ? (
              <>
                <p className="amm-palmistry101-detail-kicker">{selected.short}</p>
                <h3 className="amm-palmistry101-detail-title">{selected.title}</h3>
                <p className="amm-palmistry101-detail-body">{selected.explanation}</p>
                <div className="amm-palmistry101-detail-block">
                  <h4 className="amm-palmistry101-detail-h">What variations may suggest</h4>
                  <p>{selected.variations}</p>
                </div>
                <div className="amm-palmistry101-detail-block">
                  <h4 className="amm-palmistry101-detail-h">What not to over-assume</h4>
                  <p>{selected.caution}</p>
                </div>
                <div className="amm-palmistry101-detail-prompt">
                  <h4 className="amm-palmistry101-detail-h">What to observe</h4>
                  <p>{selected.observe}</p>
                </div>
              </>
            ) : (
              <div className="amm-palmistry101-detail-locked">
                <p className="amm-palmistry101-detail-kicker">{selected.short}</p>
                <h3 className="amm-palmistry101-detail-title">{selected.title}</h3>
                <p className="amm-palmistry101-detail-body amm-palmistry101-detail-teaser">
                  {selected.explanation.slice(0, 220)}
                  {selected.explanation.length > 220 ? '…' : ''}
                </p>
                <div className="amm-palmistry101-detail-unlock">
                  <p className="amm-palmistry101-detail-unlock-text">
                    The full interpretation for this area — and the complete hand map — is part of Palmistry
                    101.
                  </p>
                  {onRequestPurchase && (
                    <button
                      type="button"
                      className="amm-palmistry101-btn amm-palmistry101-btn-secondary amm-palmistry101-detail-unlock-btn"
                      onClick={onRequestPurchase}
                    >
                      View enrollment options
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
